"use client";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import RangeSlider from "@/components/RangeSlider/RangeSlider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import useDeliveryMessage from "@/hooks/useDeliveryMessage";
import { capitalizeFirstLetter, formatDate } from "@/lib/utils";
import { setDeliveryRouteDetails } from "@/redux/slices/deliveryRouteSlice";
import { useQueryParams } from "@/utils/useQueryParams";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const Sidebar = ({
  categorySlug,
  categoryId,
  isValidity = false,
  rebateId,
  isGetItFast = true,
  isSpecialOffers = true,
  deliveryDetails,
  isBrandPage,
  isShop,
  brandIds: selectedBrandIds,
  filterDetails,
  toggleVisibility,
  totalProductCount,
  isWillItFIlterShowing,
}) => {
  const {
    brandIds,
    type,
    onDisplay,
    isAvailabilty,
    fieldIds,
    fieldValueIds,
    categoryIds,
    validUntil,
    isExpired,
    storeId,
    setQueryParams,
    clearAllParams,
    zipCode,
    deliveryDate,
    onSale,
    clearance,
    rebateAvailable,
    deliveryRouteId,
    height,
    width,
    depth,
    totalCapacity,
    iceCapacity,
    dailyIceProduction,
    ovenCapacity,
    washerHeight,
    washerWidth,
    washerDepth,
    dryerHeight,
    dryerWidth,
    dryerDepth,
    capacity,
    minPrice,
    maxPrice,
    setProductCategoryIds
  } = useQueryParams();
  const { zipCode: currentZipCode = "94545", currentStoreId } =
    useSelector((state) => state?.userLocation) || {};

  const { routeClassId } = deliveryDetails || {};

  const {
    brands,
    data: filtersList,
    price,
    staticFiltersCounts,
    matchedBrandIds,
    typeList,
    appliances
  } = filterDetails || {};
  
  const {
    clearanceCount = null,
    inStockCount = null,
    onDisplayCount = null,
    onSaleCount = null,
    rebatesCount = null,
  } = staticFiltersCounts || {};
  
  // const brandsList = data?.brands || [];
  // const categoriesList = data?.categories || [];
  // const filtersList = data?.data || [];

  const { minDisplayPrice, maxDisplayPrice } = price || {};
  const [sliderValue, setSliderValue] = useState([
    Math.round(minDisplayPrice),
    Math.round(maxDisplayPrice),
  ]);

  const handlePriceChange = (min, max) => {
    injectParams({ minPrice: min, maxPrice: max });
  };

  useEffect(() => {
    const queryMin = Number(minPrice) || minDisplayPrice;
    const queryMax = Number(maxPrice) || maxDisplayPrice;

    const isWithinBounds =
      queryMin >= minDisplayPrice && queryMax <= maxDisplayPrice;

    if (isWithinBounds) {
      setSliderValue([queryMin, queryMax]);
    } else {
      setSliderValue([
        Math.round(minDisplayPrice),
        Math.round(maxDisplayPrice),
      ]);
      setQueryParams({ minPrice: "", maxPrice: "" });
    }
  }, [minDisplayPrice, maxDisplayPrice, minPrice, maxPrice]);

  // Get current brandIds from the query string
  const currentBrandIds = brandIds ? brandIds.split(",") : [];
  const currentTypeIds = type ? type.split(",") : [];
  const currentFieldIds = fieldIds ? fieldIds.split(",") : [];
  const currentCategoriesIds = setProductCategoryIds ? setProductCategoryIds.split(",") : [];
  const currentFieldValueIds = fieldValueIds ? fieldValueIds.split(",") : [];
  const currentRangeFilterValues = {
    height: height ? height.split(",") : [],
    width: width ? width.split(",") : [],
    depth: depth ? depth.split(",") : [],
    totalCapacity: totalCapacity ? totalCapacity.split(",") : [],
    iceCapacity: iceCapacity ? iceCapacity.split(",") : [],
    dailyIceProduction: dailyIceProduction ? dailyIceProduction.split(",") : [],
    ovenCapacity: ovenCapacity ? ovenCapacity.split(",") : [],
    washerHeight: washerHeight ? washerHeight.split(",") : [],
    washerWidth: washerWidth ? washerWidth.split(",") : [],
    washerDepth: washerDepth ? washerDepth.split(",") : [],
    dryerHeight: dryerHeight ? dryerHeight.split(",") : [],
    dryerWidth: dryerWidth ? dryerWidth.split(",") : [],
    dryerDepth: dryerDepth ? dryerDepth.split(",") : [],
    capacity: capacity ? capacity.split(",") : [],
  };

  const [searchQuery, setSearchQuery] = useState("");

  const injectParams = (params) => setQueryParams({ page: 1, ...params });

  // Filtered list based on search query
  const filteredBrands = brands?.filter(
    (brand) =>
      brand?.brandName &&
      brand.brandName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const handleBrandChange = (brandId) => {
    const stringBrandId = String(brandId);
    let updatedBrandIds = currentBrandIds.includes(stringBrandId)
      ? currentBrandIds.filter((id) => id !== stringBrandId)
      : [...currentBrandIds, stringBrandId];

    let params = { brandIds: updatedBrandIds.toString() };
    const isMatchedBrandRemoved = matchedBrandIds?.includes(brandId);
    const remainingMatchedBrands = updatedBrandIds.filter((id) =>
      matchedBrandIds?.includes(id)
    );
    if (isMatchedBrandRemoved && remainingMatchedBrands.length === 0) {
      params.keyword = "";
    }
    injectParams(params);
  };

  // Inject matchedBrandIds ONLY IF brandIds are empty (Initial Load)
  useEffect(() => {
    if (!matchedBrandIds?.length) return;
    // If user has already made a selection, don't override it
    if (currentBrandIds.length) return;
    injectParams({ brandIds: matchedBrandIds?.map(String).toString() });
  }, [matchedBrandIds]);

  const handleRebateTypeChange = (typeId) => {
    const stringTypeId = String(typeId);
    let updatedTypeIds = currentTypeIds.includes(stringTypeId)
      ? currentTypeIds.filter((id) => id !== stringTypeId)
      : [...currentTypeIds, stringTypeId];
    injectParams({ type: updatedTypeIds.toString() });
  };

  const handleCategoryChange = (setProductCategoryIds) => {
    const updatedCategoryIds = currentCategoriesIds.includes(String(setProductCategoryIds))
      ? currentCategoriesIds.filter((id) => id !== String(setProductCategoryIds))
      : [...currentCategoriesIds, setProductCategoryIds?.toString()];
    injectParams({ setProductCategoryIds: updatedCategoryIds.toString() });
  };

  const handleDynamicFiltersChange = (fieldId, fieldValues) => {
    const updatedFieldIds = Array.from(
      new Set([...currentFieldIds, fieldId?.toString()])
    ).join(",");
    const updatedFieldValueIds = currentFieldValueIds.includes(
      String(fieldValues)
    )
      ? currentFieldValueIds.filter((id) => id !== String(fieldValues)) // Remove if it exists
      : [...currentFieldValueIds, fieldValues?.toString()];

    injectParams({
      fieldIds: updatedFieldIds.toString(),
      fieldValueIds: updatedFieldValueIds.toString(),
      filterSubCategoryId: categoryId,
    });
  };

  const [isActive, setIsActive] = useState(false);
  const [isActiveMap, setIsActiveMap] = useState({});

  // Toggle the state
  const toggleClass = () => {
    setIsActive((prevState) => !prevState);
  };

  const toggleActiveClass = (index) => {
    setIsActiveMap((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const rebatesFilterCount =
    rebatesCount > 0 &&
    (rebatesCount < totalProductCount || rebateAvailable === "true");
  const clearanceFilterCount =
    clearanceCount > 0 &&
    (clearanceCount < totalProductCount || clearance === "true");
  const onSaleFilterCount =
    onSaleCount > 0 && (onSaleCount < totalProductCount || onSale === "true");

  const hasSpecialOffers =
    rebatesFilterCount || clearanceFilterCount || onSaleFilterCount;

  const inStockFilterCount =
    inStockCount > 0 &&
    (inStockCount < totalProductCount || isAvailabilty === "true");
  const onDisplayFilterCount =
    onDisplayCount > 0 &&
    (onDisplayCount < totalProductCount || onDisplay === "true");

  const hasGetItFast =
    inStockFilterCount || onDisplayFilterCount || routeClassId;

  const handleClearFilters = () => {
    clearAllParams({ removeKeyword: Boolean(matchedBrandIds?.length) });
    setSliderValue([Math.round(minDisplayPrice), Math.round(maxDisplayPrice)]);
    setSearchQuery("");
  };

  const handleRangeFiltersChange = (fieldKey, from, to) => {
    let existingValues = currentRangeFilterValues[fieldKey] || [];
    let rangeValue = `${from}-${to}`;
    if (existingValues.includes(rangeValue)) {
      existingValues = existingValues.filter((val) => val !== rangeValue);
    } else {
      existingValues.push(rangeValue);
    }
    injectParams({
      [fieldKey]:
        existingValues.length > 0 ? existingValues.join(",") : undefined,
    });
  };

  const getTypeLabel = (type) => {
    if (type === "exclusive") {
      return "Airport Home Appliance Exclusive";
    }
    return capitalizeFirstLetter(type);
  };

  const customSortOrder = ["exclusive", "nationwide", "bodewell"];

  return (
    <div className="p-0 lg:px-[15px] lg:pb-[15px] rounded-20 overflow-hidden border border-gray5 bg-white lg:overflow-auto lg:border-0 lg:rounded-none">
      <div className="   pr-[8px] pb-6 pt-4  relative ">
        <div
          className={`pr-[10px]  overflow-auto pl-[16px]  sidebar ${
            !isValidity
              ? isWillItFIlterShowing
                ? "max-h-PLPSideBar lg:max-h-mobilePLPSideBar "
                : "max-h-PLPSideBar lg:max-h-[calc(100vh-300px)]"
              : "lg:max-h-mobileRebateSideBar max-h-RebateSidebar "
          } 
          `}
        >
          <div
            className="flex justify-end pb-3 cursor-pointer sticky top-[-1px] z-[1] bg-white "
            onClick={handleClearFilters}
          >
            <span className="text-xs font-semibold ">Clear Filters</span>
          </div>
          {/* Get it Fast */}
          {isGetItFast && hasGetItFast && (
            <Accordion
              collapsible
              type="single"
              className="mb-4"
              defaultValue={"item-1"}
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className=" pt-0 text-[16px] pb-1 font-semibold">
                  Get it Fast
                </AccordionTrigger>
                <AccordionContent className="">
                  <div className="pl-3 pr-2 min-[25px]">
                    <div className="" type="multiple">
                      <div value="item-1" className="bottom-0 border-none">
                        <div>
                          <div className="mt-[5px] max-h-48 overflow-y-auto sidebar">
                            {/* Checkbox for "In Stock Today" */}
                            {inStockFilterCount && (
                              <div className="flex items-center justify-between py-[7px]">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="nextDayDelivery"
                                    onCheckedChange={(value) =>
                                      injectParams({
                                        isAvailabilty: value ? "true" : "",
                                      })
                                    }
                                    checked={isAvailabilty === "true"}
                                  />
                                  <label
                                    htmlFor="nextDayDelivery"
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    In Stock Today
                                  </label>
                                </div>
                                ({inStockCount})
                              </div>
                            )}
                            {/* Get it Tomorrow Start" */}
                            {routeClassId && (
                              <GetItTomorrow
                                deliveryDetails={deliveryDetails || {}}
                                currentZipCode={currentZipCode}
                                injectParams={injectParams}
                                zipCode={zipCode}
                                deliveryRouteId={deliveryRouteId}
                              />
                            )}
                            {/* Get it Tomorrow End" */}
                            {/* On Display at Store Today" */}
                            {onDisplayFilterCount && (
                              <div className="flex items-center justify-between py-[7px]">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="onDisplayFilter"
                                    checked={onDisplay === "true"}
                                    onCheckedChange={(value) =>
                                      injectParams({
                                        onDisplay: value ? "true" : "",
                                        storeId: value ? currentStoreId : "",
                                      })
                                    }
                                  />
                                  <label
                                    htmlFor="onDisplayFilter"
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    On Display at Store Today
                                  </label>
                                </div>
                                ({onDisplayCount})
                              </div>
                            )}
                            {/* On Display at Store Today End */}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Get it Fast End */}

          {/* TypeList Start */}
          {typeList && typeList?.length > 0 && (
            <Accordion
              collapsible
              type="single"
              className="mb-3"
              defaultValue="item-1"
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className="pb-[14px] pt-0 text-[16px] font-semibold">
                  Type
                </AccordionTrigger>
                <AccordionContent>
                  <div className="border-gray-200 border my-[10px] mb-[15px] rounded-[10px] py-2 px-3">
                    <div className="sidebar relative max-h-48 overflow-y-auto pr-[6px]">
                      {typeList?.length > 0 ? (
                        [...typeList]
                          .sort((a, b) => {
                            const indexA = customSortOrder.indexOf(a.type);
                            const indexB = customSortOrder.indexOf(b.type);
                            if (indexA === -1 && indexB === -1) {
                              return a.type.localeCompare(b.type);
                            }
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;
                            return indexA - indexB;
                          })
                          .map((type, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between py-[7px]"
                            >
                              <div className="flex items-center space-x-2 mr-2">
                                <Checkbox
                                  id={`types-${index}`}
                                  onCheckedChange={() =>
                                    handleRebateTypeChange(type?.type)
                                  }
                                  checked={currentTypeIds.includes(
                                    `${type?.type}`
                                  )}
                                />
                              </div>
                              <div className="flex justify-between items-center w-full">
                                <label
                                  htmlFor={`types-${index}`}
                                  className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {getTypeLabel(type?.type)}
                                </label>
                                <span className="text-[14px] leading-[19px]">
                                  ({type?.count || 0})
                                </span>
                              </div>
                            </div>
                          ))
                      ) : (
                        <p className="text-gray6 text-[16px] p-4">
                          No type found
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
          {/* TypeList End */}

          {/* Brands */}
          {!isBrandPage && !isShop && brands?.length > 0 && (
            <Accordion
              collapsible
              type="single"
              className="mb-3"
              defaultValue="item-1"
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className="pb-[14px] pt-0 text-[16px] font-semibold">
                  Brand
                </AccordionTrigger>

                <AccordionContent>
                  {/* Search Input */}
                  <Input
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-[44px] px-7 border-gray5 rounded-[8px] text-[18px]"
                  />
                  <div className="border-gray-200 border my-[10px] mb-[15px] rounded-[10px] py-2 px-3">
                    <div
                      className={`sidebar relative max-h-48 overflow-y-auto pr-[6px]`}
                    >
                      {filteredBrands?.length > 0 ? (
                        filteredBrands
                          .slice(0, isActive ? undefined : 4)
                          .map((brand, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between py-[7px]"
                            >
                              <div className="flex items-center space-x-2 mr-2">
                                <Checkbox
                                  id={`brands-${index}`}
                                  onCheckedChange={() =>
                                    handleBrandChange(brand?.brandId)
                                  }
                                  checked={currentBrandIds.includes(
                                    `${brand?.brandId}`
                                  )}
                                />
                              </div>
                              <div className="flex justify-between items-center w-full">
                                <label
                                  htmlFor={`brands-${index}`}
                                  className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {capitalizeFirstLetter(brand?.brandName)}
                                </label>
                                <span className="text-[14px] leading-[19px]">{`(${
                                  brand?.count || 0
                                })`}</span>
                              </div>
                            </div>
                          ))
                      ) : (
                        <p className="text-gray6 text-[16px] p-4">
                          No brand found
                        </p>
                      )}
                    </div>
                    {/* Show More / Show Less Button */}
                    {filteredBrands.length > 4 && (
                      <div className="w-full text-left mt-2">
                        <button
                          onClick={toggleClass}
                          className="underline text-blue-700 text-[12px]"
                        >
                          {isActive ? "Show less" : "Show more"}
                        </button>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
          {/* Brands End */}

          {/* appliances */}
          {appliances?.length > 0 && (
            <Accordion
              collapsible
              type="single"
              className="mb-3"
              defaultValue="item-4"
            >
              <AccordionItem value="item-4">
                <AccordionTrigger className="pb-[14px] pt-0 text-[16px] font-semibold">
                  Appliances
                </AccordionTrigger>
                <AccordionContent>
                  <div className="border-gray-200 border my-[10px] mb-[15px] rounded-[10px] py-2 px-3">
                    <div
                      className={`sidebar relative max-h-48 overflow-y-auto pr-[6px]`}
                    >
                      {appliances?.map((appliance, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-[7px]"
                        >
                          <div className="flex items-center space-x-2 mr-2">
                            <Checkbox
                              id={`appliance-${index}`}
                              onCheckedChange={() =>
                                handleCategoryChange(appliance?.categoryId)
                              }
                              checked={currentCategoriesIds.includes(
                                `${appliance?.categoryId}`
                              )}
                            />
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <label
                              htmlFor={`appliance-${index}`}
                              className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {capitalizeFirstLetter(appliance?.categoryName)}
                            </label>
                            <span className="text-[14px] leading-[19px]">{`(${
                              appliance?.count || 0
                            })`}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
          {/* appliances end */}

          {/* Special Offers */}

          {isSpecialOffers && hasSpecialOffers && (
            <Accordion
              collapsible
              type="single"
              className="mb-4"
              defaultValue={"item-1"}
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className=" pt-0 text-[16px] pb-1 font-semibold">
                  Special Offers
                </AccordionTrigger>
                <AccordionContent className="">
                  <div className="pl-3 pr-2 min-[25px]">
                    <div className="" type="multiple">
                      <div value="item-1" className="bottom-0 border-none">
                        <div>
                          <div className="mt-[5px] max-h-48 overflow-y-auto sidebar">
                            {/* Checkbox for "on Sale" */}
                            {onSaleFilterCount && (
                              <div className="flex items-center justify-between py-[7px]">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="onSale"
                                    onCheckedChange={(value) =>
                                      injectParams({
                                        onSale: value ? "true" : "",
                                      })
                                    }
                                    checked={onSale === "true"}
                                  />
                                  <label
                                    htmlFor="onSale"
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    On Sale
                                  </label>
                                </div>
                                ({onSaleCount})
                              </div>
                            )}
                            {/* Checkbox for "clearance" */}
                            {clearanceFilterCount && (
                              <div className="flex items-center justify-between py-[7px]">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="clearance"
                                    onCheckedChange={(value) =>
                                      injectParams({
                                        clearance: value ? "true" : "",
                                      })
                                    }
                                    checked={clearance === "true"}
                                  />
                                  <label
                                    htmlFor="clearance"
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    Clearance
                                  </label>
                                </div>
                                ({clearanceCount})
                              </div>
                            )}
                            {/* Checkbox for "rebateAvailable" */}
                            {rebatesFilterCount && (
                              <div className="flex items-center justify-between py-[7px]">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="rebateAvailable"
                                    onCheckedChange={(value) =>
                                      injectParams({
                                        rebateAvailable: value ? "true" : "",
                                      })
                                    }
                                    checked={rebateAvailable === "true"}
                                  />
                                  <label
                                    htmlFor="rebateAvailable"
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    Rebates Available
                                  </label>
                                </div>
                                ({rebatesCount})
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
          {/* Special Offers */}

          {/* Pricing UI */}
          {minDisplayPrice && Number(maxDisplayPrice) > 0 && (
            <div type="multiple" className="mb-6" defaultValue="item-1">
              <div value="item-1">
                <div className="pb-[5px] pt-0 text-[16px]  font-semibold">
                  Pricing
                </div>
                <div>
                  <div className="mt-[5px]">
                    <RangeSlider
                      min={Math.round(minDisplayPrice)}
                      max={Math.round(maxDisplayPrice)}
                      onPriceChange={([min, max]) => {
                        handlePriceChange(min, max);
                      }}
                      value={sliderValue}
                      setValue={setSliderValue}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Pricing UI END */}
          {filtersList &&
            filtersList?.length > 0 &&
            filtersList?.map((filter, index) => (
              <Accordion collapsible key={index} type="single" className="mb-4">
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="pt-0 text-[16px] my-[6px] pb-1 font-semibold">
                    {filter?.titleName}
                  </AccordionTrigger>
                  {filter.fields &&
                    filter.fields.length > 0 &&
                    filter.fields.map((childFilter, childIndex) => {
                      const hasMoreThanFour =
                        childFilter?.fieldValues?.length > 4;
                      const isExpanded =
                        isActiveMap[`${index}-${childIndex}`] || false;
                      const isRangeFilter =
                        // (filter?.titleName === "Dimensions" ||
                        // filter?.titleName === "Capacity" || filter?.titleName === "Oven Features" || filter?.titleName === "Washer Dimensions" || filter?.titleName === "Dryer Dimensions") &&
                        childFilter?.isRange;
                      return (
                        <AccordionContent
                          key={childIndex}
                          className="border-gray-200 border my-[10px] mb-[15px] rounded-[10px] py-2"
                        >
                          <div className="pl-3 pr-2 min-[25px]">
                            <div>
                              {!childFilter?.isBinary ? (
                                <>
                                  <div className="text-black1 p-0 my-2 text-[14px] leading-[24px]">
                                    {childFilter?.fieldName}
                                  </div>
                                  <div>
                                    <div
                                      className={`mt-[5px] sidebar max-h-48 overflow-y-auto pr-[6px]`}
                                    >
                                      {childFilter?.fieldValues &&
                                        childFilter?.fieldValues.length > 0 &&
                                        childFilter?.fieldValues
                                          .slice(0, isExpanded ? undefined : 4)
                                          .map(
                                            (subChildFilter, subChildIndex) => (
                                              <div
                                                key={subChildIndex}
                                                className="flex items-center justify-between py-[7px]"
                                              >
                                                <div className="flex items-center space-x-2 mr-2">
                                                  {isRangeFilter ? (
                                                    <Checkbox
                                                      id={`${childFilter?.fieldKey}-${subChildIndex}`}
                                                      onCheckedChange={() =>
                                                        handleRangeFiltersChange(
                                                          childFilter?.fieldKey,
                                                          subChildFilter?.from,
                                                          subChildFilter?.to
                                                        )
                                                      }
                                                      checked={currentRangeFilterValues[
                                                        childFilter?.fieldKey
                                                      ]?.includes(
                                                        `${subChildFilter?.from}-${subChildFilter?.to}`
                                                      )}
                                                    />
                                                  ) : (
                                                    <Checkbox
                                                      id={`${childFilter?.fieldName}-${subChildIndex}`}
                                                      onCheckedChange={() =>
                                                        handleDynamicFiltersChange(
                                                          childFilter?.fieldId,
                                                          subChildFilter?.fieldValueId
                                                        )
                                                      }
                                                      checked={currentFieldValueIds.includes(
                                                        `${subChildFilter?.fieldValueId}`
                                                      )}
                                                    />
                                                  )}
                                                </div>
                                                <div className="flex justify-between items-center w-full">
                                                  <label
                                                    htmlFor={`${childFilter?.fieldName}-${subChildIndex}`}
                                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                  >
                                                    {subChildFilter?.fieldValue}
                                                  </label>
                                                  {subChildFilter?.count && (
                                                    <span className="text-[14px] leading-[19px]">
                                                      {`(${subChildFilter?.count})`}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            )
                                          )}
                                    </div>
                                    {hasMoreThanFour && (
                                      <div className="w-full text-left mt-2">
                                        <button
                                          onClick={() =>
                                            toggleActiveClass(
                                              `${index}-${childIndex}`
                                            )
                                          }
                                          className="underline text-blue-700 text-[12px]"
                                        >
                                          {isExpanded
                                            ? "Show less"
                                            : "Show more"}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-center justify-between py-[3px] pr-2">
                                  <label
                                    htmlFor={`${childFilter?.fieldName}-${index}`}
                                    className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {childFilter?.fieldName}
                                  </label>
                                  <Checkbox
                                    id={`${childFilter?.fieldName}-${index}`}
                                    onCheckedChange={(value) => {
                                      let fieldValueId =
                                        childFilter?.fieldValues?.find(
                                          ({ fieldValue }) =>
                                            fieldValue === "Yes"
                                        )?.fieldValueId;

                                      if (value) {
                                        handleDynamicFiltersChange(
                                          childFilter?.fieldId,
                                          fieldValueId
                                        );
                                      } else {
                                        const updatedFilter =
                                          currentFieldValueIds.includes(
                                            String(fieldValueId)
                                          ) &&
                                          currentFieldValueIds.filter(
                                            (id) => id !== String(fieldValueId)
                                          );

                                        setQueryParams({
                                          fieldValueIds: updatedFilter,
                                        });
                                      }
                                    }}
                                    checked={currentFieldValueIds.includes(
                                      `${
                                        childFilter?.fieldValues?.find(
                                          ({ fieldValue }) =>
                                            fieldValue === "Yes"
                                        )?.fieldValueId
                                      }`
                                    )}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </AccordionContent>
                      );
                    })}
                </AccordionItem>
              </Accordion>
            ))}

          {isValidity && (
            <>
              <h4 className=" pt-0 text-[16px] my-[6px] pb-1 font-semibold">
                Validity
              </h4>
              <p className="text-[15px]">Valid Until:</p>
              <DatePicker
                label="Select date"
                date={validUntil || null}
                onChange={(value) =>
                  setQueryParams({
                    validUntil: formatDate(value, "yyyy-MM-dd"),
                  })
                }
                className="max-w-[400px]"
              />

              <div className="pb-7 flex gap-[7px] items-center">
                <Checkbox
                  id="recentlyExpired"
                  checked={isExpired === "true"}
                  onCheckedChange={(value) =>
                    injectParams({
                      isExpired: value ? "true" : "false",
                    })
                  }
                />
                <label
                  htmlFor="recentlyExpired"
                  className="cursor-pointer text-[14px]"
                >
                  Recently Expired Rebates
                </label>{" "}
              </div>
            </>
          )}
          <div className=" hidden lg:block sticky bottom-0">
            <button
              className="bg-[#0A223C] text-[14px] text-white font-medium w-full flex items-center justify-center h-[53px] rounded-[8px]"
              onClick={() => {
                toggleVisibility();
              }}
            >
              Apply Selection
            </button>
          </div>
        </div>
        {/* <div className=" absolute bottom-0 right-0 left-0 h-14 bg-filterBoxShadow "></div> */}
      </div>
    </div>
  );
};

export default Sidebar;

const GetItTomorrow = ({
  deliveryDetails,
  currentZipCode,
  injectParams,
  zipCode,
  deliveryRouteId: selectedRouteId,
}) => {
  const dispatch = useDispatch();
  const { daysActive, routeClassId, disabledDates, deliveryRouteId } =
    deliveryDetails;
  // const { getDeliveryMessage } = useDeliveryMessage();
  const { deliveryMessage, deliveryDate } = useDeliveryMessage({
    routeClassId,
    daysActive,
    disabledDates,
  });

  useEffect(() => {
    if (deliveryDate && deliveryMessage) {
      dispatch(
        setDeliveryRouteDetails({
          deliveryDate,
          deliveryMessage,
          zipCode: currentZipCode,
        })
      );
    }
  }, [dispatch, deliveryDate, deliveryMessage]);

  const formattedTomorrowDate = formatDate(deliveryDate, "EEE, MMM dd");

  return (
    <div className="flex items-center justify-between py-[7px]">
      <div className="flex items-start space-x-2">
        <Checkbox
          id="getItTomorrow"
          checked={!!zipCode && !!selectedRouteId}
          onCheckedChange={(value) =>
            injectParams({
              zipCode: value ? currentZipCode : "",
              deliveryRouteId: value ? deliveryRouteId : "",
              deliveryDate: value ? formatDate(deliveryDate, "yyyy-MM-dd") : "",
            })
          }
        />
        <label
          htmlFor="getItTomorrow"
          className="text-[14px] leading-[19px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Get it {deliveryMessage} - {formattedTomorrowDate}
        </label>
      </div>
    </div>
  );
};
